import { useMemo } from "react";

interface CustomDomainInfo {
  /** Whether the app is currently running on a custom domain */
  isCustomDomain: boolean;
  /** The extracted prefix from the current hostname, or null */
  currentPrefix: string | null;
}

/**
 * Detects whether the current hostname is a custom domain subdomain.
 * Uses VITE_APP_URL to determine the base domain for the current environment.
 */
export function useCustomDomain(): CustomDomainInfo {
  return useMemo(() => {
    const baseDomain = getBaseDomain();
    if (!baseDomain) return { isCustomDomain: false, currentPrefix: null };

    const hostname = window.location.hostname;
    if (hostname === baseDomain) {
      return { isCustomDomain: false, currentPrefix: null };
    }

    const suffix = `.${baseDomain}`;
    if (hostname.endsWith(suffix)) {
      const prefix = hostname.slice(0, -suffix.length);
      if (prefix.length > 0) {
        return { isCustomDomain: true, currentPrefix: prefix };
      }
    }

    return { isCustomDomain: false, currentPrefix: null };
  }, []);
}

/**
 * Constructs the full custom domain URL for a given prefix.
 * Returns null if no prefix or no VITE_APP_URL configured.
 */
export function getCustomDomainUrl(prefix: string | null): string | null {
  if (!prefix) return null;
  const baseDomain = getBaseDomain();
  if (!baseDomain) return null;
  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : "";
  return `${protocol}//${prefix}.${baseDomain}${port}`;
}

function getBaseDomain(): string | null {
  // VITE_APP_URL is set per environment (e.g., https://skysupport.skypoint.ai for prod)
  const appUrl = import.meta.env.VITE_APP_URL;
  if (appUrl) {
    try {
      return new URL(appUrl).hostname;
    } catch {
      // fall through
    }
  }
  return null;
}
